import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { CirclePicker } from 'react-color';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
  Grid,
  FormControl,
  Card,
  CardMedia,
  CardActions,
  Button,
  Popover,
  Select,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import { uploadLogoImage } from 'utils/themes/uploadLogoImage';
import EditIcon from '@mui/icons-material/Edit';
import { THEME_VALUES, BUTTONS, THEME_RADIUS_INDICATOR, THEME_TYPES } from 'utils/constants';

export const ThemePreferencesControls = ({
  logo,
  setLogo,
  borderRadius,
  setBorderRadius,
  primaryColor,
  setPrimaryColor,
  type,
  setIsCommonThemesListOpened,
}) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const handlePaletteClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handlePaletteClose = () => {
    setAnchorEl(null);
  };
  const handleColorChange = (color) => {
    setPrimaryColor(color.hex);
    handlePaletteClose();
  };
  const openThemeList = () => {
    setIsCommonThemesListOpened(true);
  };

  const [isUploadingImage, setIsUploadingImage] = useState(false);

  const uploadImage = async (event) => {
    await uploadLogoImage(event, setIsUploadingImage, setLogo);
  };

  const MUTheme = createTheme({
    shape: {
      borderRadius: Number(borderRadius.replace('px', '')),
    },
    palette: { primary: { main: primaryColor || THEME_VALUES.defaultPrimaryColor } },
  });

  const handleIsRoundedChange = (event) => {
    setBorderRadius(event.target.value);
  };

  return (
    <div className="theme-preferences-container">
      <Card className="logo-card">
        {isUploadingImage ? (
          <CircularProgress />
        ) : (
          <CardMedia component="img" image={logo} classes={{ root: 'logo' }} />
        )}
        <CardActions>
          <label htmlFor={`btn-upload-${type}`} className="logo-upload-label">
            <input
              id={`btn-upload-${type}`}
              name={`btn-upload-${type}`}
              className="logo-input"
              type="file"
              onChange={uploadImage}
            />
            <Button component="span" color="primary" startIcon={<EditIcon />}>
              {BUTTONS.edit}
            </Button>
          </label>
        </CardActions>
      </Card>

      <Grid className="style-container">
        <ThemeProvider theme={MUTheme}>
          <Button
            color="primary"
            onClick={handlePaletteClick}
            variant="contained"
            startIcon={<EditIcon />}
            classes={{ root: 'color-button' }}
          >
            {BUTTONS.color}
          </Button>
          <Popover
            color="primary"
            open={!!anchorEl}
            anchorEl={anchorEl}
            onClose={handlePaletteClose}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
          >
            <CirclePicker
              color={primaryColor}
              onChangeComplete={handleColorChange}
              colors={THEME_VALUES.defaultColors}
              className="theme-editor-color-picker"
              width="266px"
            />
          </Popover>
          {type !== THEME_TYPES.common ? (
            <Button
              color="primary"
              variant="contained"
              onClick={openThemeList}
              classes={{ root: 'theme-list-button' }}
            >
              {BUTTONS.themeList}
            </Button>
          ) : null}
        </ThemeProvider>
        <FormControl className="radius-container">
          <Select value={borderRadius} onChange={handleIsRoundedChange}>
            <MenuItem value={THEME_VALUES.roundedRadius}>{THEME_RADIUS_INDICATOR.rounded}</MenuItem>
            <MenuItem value={THEME_VALUES.squaredRadius}>{THEME_RADIUS_INDICATOR.squared}</MenuItem>
          </Select>
        </FormControl>
      </Grid>
    </div>
  );
};

ThemePreferencesControls.propTypes = {
  logo: PropTypes.string.isRequired,
  setLogo: PropTypes.func.isRequired,
  borderRadius: PropTypes.string.isRequired,
  setBorderRadius: PropTypes.func.isRequired,
  primaryColor: PropTypes.string.isRequired,
  setPrimaryColor: PropTypes.func.isRequired,
  type: PropTypes.string,
  setIsCommonThemesListOpened: PropTypes.func,
};
